import { Injectable, computed, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, pipe, switchMap, tap, withLatestFrom } from 'rxjs';
import { FrequencyType, Hour } from '../models/FrequencyType.model';
import { OptionsList } from '../models/optionsList.model';
import { Question } from '../models/question.model';
import {
    QuestionnaireCategory,
    QuestionnaireCategoryCountDto,
} from '../models/questionnaireCategory.model';
import { ExposureFieldDto } from '../models/exposureField';
import { TableFieldDto } from '../models/tableField.model';
import { ALLQuestionTypes, QuestionType } from '../models/questionType.model';
import { OptionsListApiService } from '../features/collections/services/options-list-api.service';
import { QuestionnaireApiService } from '../services/questionnaire-api.service';
import { RemindersApiService } from '../services/reminders-api.service';
import { UsersApiService } from '../services/users-api.service';
import { User, UserDetails, Role } from '../models/user.model';
import { Reminder } from '../models/reminder';
import { DistributionGroup } from '../models/distribution-group.model';
import { DistributionGroupApiService, GroupOption } from '../services/distribution-group-api.service';
import { CollectionQuestionApiService } from '../features/collections/services/collection-question-api.service';
import * as countryCodes from 'country-codes-list';
import { CountryCallingCode } from '../models/country-calling-code.model';
import { GlobalService } from '../shared/services/global-service';
import { TostesStore } from './tostes-store';
import { TostPositionEnum, TostTypeEnum } from '../shared/models/tostes.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from 'src/environment';


export interface GlobalState {
    readonly users: User[];
    readonly frequency: FrequencyType[];
    readonly categories: QuestionnaireCategoryCountDto[];
    readonly allCategories: QuestionnaireCategory[];
    readonly exposureFields: ExposureFieldDto[];
    readonly tableFields: TableFieldDto[];
    readonly houers: Hour[];
    readonly optionLists: OptionsList[];
    readonly loading: boolean;
    readonly questionTypes: ALLQuestionTypes;
    readonly questionTypesToModular: QuestionType[];
    readonly language: string;
    readonly collectionSmartQuestions: Question[];
    readonly dir: string;
    readonly loggedUserDetails?: UserDetails;
    readonly loadingAnswer: boolean;
    readonly loadingCollectionQuestions: boolean;
    readonly loadingTypes: boolean;
    readonly refreshGrops: boolean;
    readonly refreshLists: boolean;
    readonly refreshReminder: boolean;
    readonly refreshQuestionnaires?: boolean;
    readonly approvers?: User[];
    readonly groupTypes?: GroupOption[];
    readonly countApprovals?: number;
    readonly isOpenStatusFlow?: boolean;
    error?: string;
    names: User[];
    countryCallingCode: CountryCallingCode[];
}

@Injectable({ providedIn: 'root' })
export class GlobalStore extends ComponentStore<GlobalState> {
    readonly isAmdocs$ = this.select(({ }) => true);


    #globalService = inject(GlobalService);

    #questionnaireApiService = inject(QuestionnaireApiService);
    #reminderApiService = inject(RemindersApiService);
    #distributionGroupService = inject(DistributionGroupApiService);
    #translate = inject(TranslateService);
    #optionsListApiService = inject(OptionsListApiService);
    #useraApiService = inject(UsersApiService);
    #collectionQuestionsApiService = inject(CollectionQuestionApiService);
    #tostesStore = inject(TostesStore);

    readonly names$ = this.select((x) => x.names);
    readonly users$ = this.select(({ users }) => users);
    readonly countryCallingCode$ = this.select(
        ({ countryCallingCode }) => countryCallingCode
    );

    constructor() {
        var houers: Hour[] = [];
        for (let hour = 0; hour < 24; hour++) {
            // for (let minutes = 0; minutes < 2; minutes++) {
            // , minutes: minutes * 30+ minutes* 2
            var h = { id: hour, hour: hour } as Hour;
            houers.push(h);
            // }
        }
        super({
            users: [],
            questionTypes: { texts: [], questions: [], buttons: [] },
            categories: [],
            allCategories: [],
            exposureFields: [],
            tableFields: [],
            frequency: [],
            names: [],
            optionLists: [],
            questionTypesToModular: [],
            collectionSmartQuestions: [],
            houers: houers,
            language: 'en',
            dir: 'ltr',
            loading: false,
            loadingAnswer: true,
            loadingCollectionQuestions: true,
            loadingTypes: true,
            refreshReminder: false,
            // refreshQuestionnaires: false,
            refreshGrops: false,
            refreshLists: false,
            countryCallingCode: [],
            isOpenStatusFlow: false
        });
        this.fetchQuestionnaireCategories();
        this.fetchAllCategories();
        if (environment.apiExposureEnabled == true) {
            this.fetchExposureFields();
        }
        this.fetchTableFields();
        this.loadNames();
        this.fetchUserDetails();
        this.fetchCountryCallingCode();
        if (environment.featurs.approvalRequired)
            this.fetchCountApprovals();
    }
    readonly categories$ = this.select(({ categories }) => categories);
    readonly allCategories$ = this.select(({ allCategories }) => allCategories);
    readonly exposureFields$ = this.select(({ exposureFields }) => exposureFields);
    readonly tableFields$ = this.select(({ tableFields }) => tableFields);
    readonly frequency$ = this.select(({ frequency }) => frequency);
    readonly houers$ = this.select(({ houers }) => houers);
    readonly questionTypes$ = this.select(({ questionTypes }) => questionTypes);
    readonly questionTypesToModular$ = this.select(({ questionTypesToModular }) => questionTypesToModular);
    readonly optionLists$ = this.select(({ optionLists }) => optionLists);
    readonly loading$ = this.select(({ loading }) => loading);
    readonly language$ = this.select(({ language }) => language);
    readonly isHeSignal$ = toSignal(this.select(({ language }) => language === "he"));
    readonly dir$ = this.select(({ dir }) => dir);
    readonly error$ = this.select(({ error }) => error);
    readonly isRoleAdmin$ = this.select(({ loggedUserDetails }) => loggedUserDetails?.isAdmin);
    readonly loggedUserDetails$ = this.select(({ loggedUserDetails }) => loggedUserDetails);
    readonly loadingAnswer$ = this.select(({ loadingAnswer }) => loadingAnswer);
    readonly loadingCollection$ = this.select(({ loadingCollectionQuestions }) => loadingCollectionQuestions);
    readonly loadingTypes$ = this.select(({ loadingTypes }) => loadingTypes);
    readonly refreshGrops$ = this.select(({ refreshGrops }) => refreshGrops);
    readonly refreshLists$ = this.select(({ refreshLists }) => refreshLists);
    readonly refreshReminder$ = this.select(({ refreshReminder }) => refreshReminder);
    readonly refreshQuestionnaires$ = this.select(({ refreshQuestionnaires }) => refreshQuestionnaires);
    readonly collection$ = this.select(({ collectionSmartQuestions }) => collectionSmartQuestions);
    readonly isDisplayApprovals$ = this.select((x) => x.loggedUserDetails?.isDisplayApprovals)
    readonly isApproverManager$ = this.select((x) => x.loggedUserDetails?.roles.includes(Role.APPROVING_MANAGER))
    readonly isPayrollRepresentative$ = this.select((x) => x.loggedUserDetails?.roles.includes(Role.PAYROLL_REPRESENTATIVE))
    readonly isAccountingRepresentative$ = this.select((x) => x.loggedUserDetails?.roles.includes(Role.ACCOUNTING_REPRESENTATIVE))
    readonly isBillingRepresentative$ = this.select((x) => x.loggedUserDetails?.roles.includes(Role.BILLING_REPRESENTATIVE))
    readonly isFreeApproverSelection$ = this.select((x) => x.loggedUserDetails?.roles.includes(Role.FTEE_APPROVER_SELECTION) ?? false);
    readonly approvers$ = this.select(({ approvers }) => approvers)
    readonly groupTypes$ = this.select(({ groupTypes }) => groupTypes)
    readonly countApprovals$ = this.select(({ countApprovals }) => countApprovals)
    readonly isOpenStatusFlow$ = this.select(({ isOpenStatusFlow }) => isOpenStatusFlow)
    readonly isRoleAdmin = toSignal(this.isRoleAdmin$);
    readonly isApproverManager = toSignal(this.isApproverManager$);
    readonly isPayrollRepresentative = toSignal(this.isPayrollRepresentative$);
    readonly isAccountingRepresentative = toSignal(this.isAccountingRepresentative$);
    readonly isBillingRepresentative = toSignal(this.isBillingRepresentative$);
    readonly showTabApprovers = computed(() => {
        return this.isRoleAdmin() || this.isApproverManager();
    });

    readonly showTabRecords = computed(() => {
        return this.isRoleAdmin() ||
            this.isPayrollRepresentative() ||
            this.isAccountingRepresentative() ||
            this.isBillingRepresentative();
    });
    readonly updatLanguage = this.updater((state, lang: string) => ({
        ...state,
        language: lang,
    }));
    readonly updateLoadingAnswer = this.updater((state, load: boolean) => ({
        ...state,
        loadingAnswer: load,
    }));
    readonly refreshQuestionnaireAfterUpdate = this.updater(
        (state) => ({
            ...state,
            refreshQuestionnaires: !state.refreshQuestionnaires,
        })
    );
    readonly refreshGroupsAfterCreate = this.updater(
        (state, refreshGrops: boolean) => ({
            ...state,
            refreshGrops,
        })
    );
    readonly refreshReminderAfterCreate = this.updater(
        (state, refreshReminder: boolean) => ({
            ...state,
            refreshReminder,
        })
    );
    readonly fetchQuestionnaireCategories = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(() =>
                this.#globalService.getCategories().pipe(
                    tapResponse(
                        (categories) => this.patchState({ categories, loading: false }),
                        (error: Error) =>
                            this.patchState({ error: error?.message, loading: false })
                    )
                )
            )
        )
    );
    readonly fetchAllCategories = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(() =>
                this.#globalService.getAllCategories().pipe(
                    tapResponse(
                        (allCategories) => this.patchState({ allCategories, loading: false }),
                        (error: Error) =>
                            this.patchState({ error: error?.message, loading: false })
                    )
                )
            )
        )
    );
    readonly fetchExposureFields = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(() =>
                this.#globalService.getExposureFields().pipe(
                    tapResponse(
                        (exposureFields) => this.patchState({ exposureFields, loading: false }),
                        (error: Error) => this.patchState({ error: error?.message, loading: false })
                    )
                )
            )
        )
    );
    readonly fetchTableFields = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(() =>
                this.#globalService.getTableFields().pipe(
                    tapResponse(
                        (tableFields) => this.patchState({ tableFields, loading: false }),
                        (error: Error) => this.patchState({ error: error?.message, loading: false })
                    )
                )
            )
        )
    );
    readonly fetchFrequency = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(() =>
                this.#reminderApiService.getFrequencyType().pipe(
                    tapResponse(
                        (frequency) => this.patchState({ frequency, loading: false }),
                        (error: Error) =>
                            this.patchState({ error: error?.message, loading: false })
                    )
                )
            )
        )
    );
    readonly fetchCollectionQuestion = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loadingCollectionQuestions: true })),
            switchMap(() =>
                this.#collectionQuestionsApiService.getAll().pipe(
                    tapResponse(
                        (collection) =>
                            this.patchState({
                                collectionSmartQuestions: collection,
                                loadingCollectionQuestions: false,
                            }),
                        (error: Error) =>
                            this.patchState({
                                error: error?.message,
                                loadingCollectionQuestions: false,
                            })
                    )
                )
            )
        )
    );
    readonly createCollectionQuestion = this.effect<Question>(
        pipe(
            switchMap((question) =>
                this.#collectionQuestionsApiService.createCollectionQuestion(question).pipe(
                    tapResponse(
                        (collection) =>
                            this.fetchCollectionQuestion(),
                        (error: Error) => {

                        }
                    )
                )
            )
        )
    );
    readonly createSavedList = this.effect<OptionsList>(
        pipe(
            switchMap((list) =>
                this.#optionsListApiService.createNewList(list).pipe(
                    tapResponse(
                        (list) => {
                            this.fetchOptionLists()
                        },
                        (error: Error) => {

                        }
                    )
                )
            )
        )
    );
    readonly loadNames = this.effect<void>(
        pipe(
            switchMap(() => this.#globalService.getNamesListForFilter()
            ),
            tap((namesList: User[]) => this.patchState({ names: namesList }))
        )
    );
    readonly changeLanguage = this.effect<void>(
        pipe(
            withLatestFrom(this.language$, this.dir$),
            tap(([_, lang, dir]) => {
                const newLang = lang == 'en' ? 'he' : 'en';
                const newDir = dir == 'rtl' ? 'ltr' : 'rtl';
                // const dir = this.#translate.instant('dir');
                document.documentElement.lang = newLang;
                document.documentElement.dir = newDir;
                this.#translate.use(newLang);
                this.patchState({
                    language: newLang,
                    dir: newDir
                });
            })
            // switchMap(() =>
            //     this.#reminderApiService.getFrequencyType().pipe(
            //         tapResponse(
            //             (frequency) => this.patchState({ frequency, loading: false }),
            //             (error: Error) =>
            //                 this.patchState({ error: error?.message, loading: false })
            //         )
            //     )
            // )
        )
    );
    readonly fetchCountryCallingCode = () => {
        const countryCallingCode = Array.from(
            new Map(
                countryCodes
                    .customArray({
                        dialCode: '+{countryCallingCode}',
                        flag: '{flag}',
                    })
                    .map((record) => [
                        record['dialCode'], // Use dialCode as the key for uniqueness
                        {
                            dialCode: record['dialCode'],
                            flag: record['flag'],
                        } as CountryCallingCode,
                    ])
            ).values() // Extract only the values to get unique records
        )
            .sort((a, b) => {
                const aNum = a.dialCode.replace('+', '').trim();
                const bNum = b.dialCode.replace('+', '').trim();

                return Number(aNum) - Number(bNum);
            });

        this.patchState({ countryCallingCode });
    };
    readonly fetchUserDetails = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(() =>
                this.#useraApiService.getLoggedUserDetails().pipe(
                    tapResponse(
                        (result) => {
                            if (result.isAdmin) {
                                this.fetchQuestionType();
                                this.fetchOptionLists();
                                this.fetchFrequency();
                                this.fetchQuestionTypeToModular();
                                this.fetchUsers();
                                this.fetchCollectionQuestion();
                                this.fetchGroupTypes();
                            }
                            if (environment.featurs.approvalRequired == "ON" && result.roles.includes(Role.FTEE_APPROVER_SELECTION))
                                this.fetchApprovers();
                            this.patchState({
                                loggedUserDetails: result,
                                loading: false,
                            });
                        },
                        (error: Error) => {
                            this.patchState({ error: error?.message, loading: false, loggedUserDetails: { firstName: "Unknown", lastName: "Unknown", isAdmin: false, id: 0, isDisplayApprovals: false, roles: [] }, })
                        })
                )
            )
        )
    );

    readonly fetchUsers = this.effect<void>(
        pipe(
            switchMap(() => this.#useraApiService.getUsers()),
            tap((users: User[]) => this.patchState({ users }))
        )
    );

    readonly fetchQuestionType = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loadingTypes: true })),
            switchMap(() =>
                this.#globalService.getQuestionTypes().pipe(
                    tapResponse(
                        (questionTypesArr) =>
                            this.patchState({
                                questionTypes: {
                                    texts: questionTypesArr.filter((x) => x.category == 1),
                                    questions: questionTypesArr.filter((x) => x.category == 2),
                                    buttons: questionTypesArr.filter((x) => x.category == 3),
                                },
                                loadingTypes: false,
                            }),
                        (error: Error) =>
                            this.patchState({ error: error?.message, loadingTypes: false })
                    )
                )
            )
        )
    );
    readonly fetchQuestionTypeToModular = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(() =>
                this.#globalService.getQuestionTypesToModulsrQuestion().pipe(
                    tapResponse(
                        (questionTypesArr) =>
                            this.patchState({
                                questionTypesToModular: [...questionTypesArr],
                                loading: false,
                            }),
                        (error: Error) =>
                            this.patchState({ error: error?.message, loading: false })
                    )
                )
            )
        )
    );
    readonly fetchOptionLists = this.effect<void>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(() =>
                this.#optionsListApiService.getAllOptionsLists().pipe(
                    tapResponse(
                        (optionLists) => this.patchState({ optionLists, loading: false }),
                        (error: Error) =>
                            this.patchState({ error: error?.message, loading: false })
                    )
                )
            )
        )
    );

    readonly createReminder1 = this.effect<Reminder>(
        pipe(
            switchMap((reminder) =>
                this.#reminderApiService.create(reminder).pipe(
                    tap((x) => {
                        this.patchState({ refreshReminder: false });
                        this.patchState({ refreshReminder: true });
                        this.#tostesStore.addTost({ guid: this.#tostesStore.generateGuid(), message: { en: "The reminder was created successfully", he: "התזכורת נוצרה בהצלחה" }, position: TostPositionEnum.BottomLeft, seconds: 5, type: TostTypeEnum.SUCCESS });
                    })
                )
            )
        )
    );

    readonly createReminder = this.effect<Reminder>(
        pipe(
            map(reminder => {
                const guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Creating a reminder...", he: "יוצר תזכורת..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD });
                return { reminder, guid };
            }),
            switchMap((props) =>
                this.#reminderApiService.create(props.reminder).pipe(
                    tapResponse((sucsess) => {
                        this.patchState({ refreshReminder: false });
                        this.patchState({ refreshReminder: true });
                        this.#tostesStore.removeTostByGuid(props.guid)
                        this.#tostesStore.addTost({ guid: "", message: { en: `The reminder was created successfully`, he: `התזכורת נוצרה בהצלחה` }, type: TostTypeEnum.SUCCESS, seconds: 5 });
                    },
                        (error) => {
                            this.#tostesStore.removeTostByGuid(props.guid)
                            this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while creating the reminder", he: "אירעה שגיאה בעת יצירת תזכורת" }, type: TostTypeEnum.ERROR, seconds: 5 });
                        })
                )
            )
        ));


    readonly createDistributionGroup = this.effect<DistributionGroup>(
        pipe(
            map(destributionGroup => {
                const guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Creating a distribution group...", he: "יוצר קבוצת הפצה..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD });
                return { destributionGroup, guid };
            }),
            switchMap((props) =>
                this.#distributionGroupService.createDistributionGroup(props.destributionGroup).pipe(
                    tapResponse((sucsess) => {
                        this.patchState({ refreshGrops: true });
                        this.#tostesStore.removeTostByGuid(props.guid)
                        this.#tostesStore.addTost({ guid: "", message: { en: `The group ${props.destributionGroup.name}  was created successfully`, he: ` הקבוצה ${props.destributionGroup.name} נוצרה בהצלחה` }, type: TostTypeEnum.SUCCESS, seconds: 5 });
                    },
                        (error) => {
                            this.patchState({ refreshGrops: true });
                            this.#tostesStore.removeTostByGuid(props.guid)
                            this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while creating the group", he: "אירעה שגיאה בעת יצירת הקבוצה" }, type: TostTypeEnum.ERROR, seconds: 5 });
                        })
                )
            )
        ));



    readonly createCollectionList = this.effect<OptionsList>(
        pipe(
            map(collectionList => {
                const guid = this.#tostesStore.generateGuid();
                this.#tostesStore.addTost({ guid, message: { en: "Creating a list...", he: "יוצר רשימה..." }, position: TostPositionEnum.BottomLeft, type: TostTypeEnum.LOAD });
                return { collectionList, guid };
            }),
            switchMap((props) =>
                this.#optionsListApiService
                    .createNewList(props.collectionList).pipe(
                        tapResponse((sucsess) => {
                            this.fetchOptionLists();
                            this.patchState({ refreshLists: true });
                            this.#tostesStore.removeTostByGuid(props.guid)
                            this.#tostesStore.addTost({ guid: "", message: { en: `The list ${props.collectionList.name}  was created successfully`, he: `הרשימה ${props.collectionList.name} נוצרה בהצלחה` }, type: TostTypeEnum.SUCCESS, seconds: 5 });
                        },
                            (error) => {
                                this.#tostesStore.removeTostByGuid(props.guid)
                                this.#tostesStore.addTost({ guid: "", message: { en: "An error occurred while creating the list", he: "אירעה שגיאה בעת יצירת הרשימה" }, type: TostTypeEnum.ERROR, seconds: 5 });
                            })
                    )
            )
        ));

    fetchGroupTypes = this.effect<void>((
        pipe(
            switchMap(() =>
                this.#distributionGroupService.getGroupTypes().pipe(
                    tapResponse(
                        (groupTypes) => { this.patchState({ groupTypes }) },
                        (error: Error) =>
                            this.patchState({ error: error?.message })
                    )
                )
            )
        )
    ));

    fetchApprovers = this.effect<void>((
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(() =>
                this.#globalService.getApprovers().pipe(
                    tapResponse(
                        (approvers) => { this.patchState({ approvers, loading: false }) },
                        (error: Error) =>
                            this.patchState({ error: error?.message, loading: false })
                    )
                )
            )
        )
    ));

    fetchCountApprovals = this.effect<void>((
        pipe(
            tap(() => this.patchState({ loading: true })),
            switchMap(() =>
                this.#globalService.getCountApprovals().pipe(
                    tapResponse(
                        (count) => { this.patchState({ countApprovals: count, loading: false }) },
                        (error: Error) =>
                            this.patchState({ error: error?.message, loading: false })
                    )
                ))
        )
    ));

    updateCountApprovals = this.updater((state, count: number) => ({
        ...state,
        countApprovals: state.countApprovals! - count
    }))

    updateIsOpenStatusFlow = this.updater((state, status: boolean) => ({
        ...state,
        isOpenStatusFlow: status
    }))
}
